import {
  CheckCircle2,
  Circle,
  Clock,
  SkipForward,
  XCircle,
} from 'lucide-react';
import React, { useMemo, useState } from 'react';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  type IntakeChaseMessage,
  IntakeChaseMessageStatus,
} from '@/services/types/intake-chase-types';

interface AutoChaseTimelineProps {
  messages: IntakeChaseMessage[];
  pcTimezone: string;
  defaultExpanded?: boolean;
}

interface DayGroup {
  dayNumber: number;
  messages: IntakeChaseMessage[];
  sentCount: number;
  totalCount: number;
  isComplete: boolean;
  hasNext: boolean;
}

/**
 * Format a datetime string in the PC's timezone.
 */
function formatTimeInTimezone(
  isoString: string,
  timezone: string,
): string {
  try {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: timezone,
      timeZoneName: 'short',
    });
  } catch {
    // Fallback if timezone is invalid
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  }
}

/**
 * Format a datetime string as a readable date in the PC's timezone.
 */
function formatDateInTimezone(
  isoString: string,
  timezone: string,
): string {
  try {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      timeZone: timezone,
    });
  } catch {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  }
}

function getMessageStatusIcon(
  status: IntakeChaseMessageStatus,
  isNext: boolean,
) {
  if (isNext) {
    return <Clock className="h-3.5 w-3.5 text-zinc-500" />;
  }

  switch (status) {
    case IntakeChaseMessageStatus.SENT:
      return <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />;
    case IntakeChaseMessageStatus.PENDING:
    case IntakeChaseMessageStatus.SENDING:
      return <Circle className="h-3.5 w-3.5 text-zinc-400" />;
    case IntakeChaseMessageStatus.SKIPPED:
      return <SkipForward className="h-3.5 w-3.5 text-zinc-400" />;
    case IntakeChaseMessageStatus.FAILED:
      return <XCircle className="h-3.5 w-3.5 text-rose-500" />;
    case IntakeChaseMessageStatus.CANCELLED:
      return <XCircle className="h-3.5 w-3.5 text-zinc-400" />;
    default:
      return <Circle className="h-3.5 w-3.5 text-zinc-400" />;
  }
}

function getMessageStatusLabel(
  status: IntakeChaseMessageStatus,
  isNext: boolean,
): string {
  if (isNext) return 'next';
  switch (status) {
    case IntakeChaseMessageStatus.SENT:
      return 'sent';
    case IntakeChaseMessageStatus.PENDING:
      return 'pending';
    case IntakeChaseMessageStatus.SENDING:
      return 'sending';
    case IntakeChaseMessageStatus.SKIPPED:
      return 'skipped';
    case IntakeChaseMessageStatus.FAILED:
      return 'failed';
    case IntakeChaseMessageStatus.CANCELLED:
      return 'cancelled';
    default:
      return status;
  }
}

function getDaySummaryIcon(group: DayGroup) {
  if (group.isComplete) {
    return <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />;
  }
  if (group.sentCount > 0) {
    return <Clock className="h-3.5 w-3.5 text-zinc-500" />;
  }
  return <Circle className="h-3.5 w-3.5 text-zinc-400" />;
}

export const AutoChaseTimeline: React.FC<AutoChaseTimelineProps> = ({
  messages,
  pcTimezone,
  defaultExpanded = false,
}) => {
  const [expandedDays, setExpandedDays] = useState<Set<number>>(() => {
    if (!defaultExpanded) return new Set<number>();
    // Expand the current active day by default
    const nextMsg = messages.find(
      (m) =>
        m.status === IntakeChaseMessageStatus.PENDING ||
        m.status === IntakeChaseMessageStatus.SENDING,
    );
    return nextMsg ? new Set([nextMsg.day_number]) : new Set<number>();
  });

  // Find the next message to send
  const nextMessageId = useMemo(() => {
    const next = messages.find(
      (m) =>
        m.status === IntakeChaseMessageStatus.PENDING ||
        m.status === IntakeChaseMessageStatus.SENDING,
    );
    return next?.id ?? null;
  }, [messages]);

  // Group messages by day
  const dayGroups = useMemo((): DayGroup[] => {
    const groupMap = new Map<number, IntakeChaseMessage[]>();
    messages.forEach((msg) => {
      const existing = groupMap.get(msg.day_number) || [];
      existing.push(msg);
      groupMap.set(msg.day_number, existing);
    });

    return Array.from(groupMap.entries())
      .sort(([a], [b]) => a - b)
      .map(([dayNumber, dayMessages]) => {
        const sentCount = dayMessages.filter(
          (m) => m.status === IntakeChaseMessageStatus.SENT,
        ).length;
        const totalCount = dayMessages.length;
        const isComplete =
          totalCount > 0 &&
          dayMessages.every(
            (m) =>
              m.status === IntakeChaseMessageStatus.SENT ||
              m.status === IntakeChaseMessageStatus.SKIPPED,
          );
        const hasNext = dayMessages.some((m) => m.id === nextMessageId);

        return { dayNumber, messages: dayMessages, sentCount, totalCount, isComplete, hasNext };
      });
  }, [messages, nextMessageId]);

  const toggleDay = (dayNumber: number) => {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(dayNumber)) {
        next.delete(dayNumber);
      } else {
        next.add(dayNumber);
      }
      return next;
    });
  };

  if (dayGroups.length === 0) {
    return (
      <p className="text-xs text-zinc-500 font-medium py-2">
        No messages scheduled yet.
      </p>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-1">
        {dayGroups.map((group) => {
          const isOpen = expandedDays.has(group.dayNumber);
          const summaryText = group.sentCount > 0
            ? `${group.sentCount}/${group.totalCount} sent`
            : `0/${group.totalCount} pending`;

          return (
            <Collapsible
              key={group.dayNumber}
              open={isOpen}
              onOpenChange={() => toggleDay(group.dayNumber)}
            >
              <CollapsibleTrigger className="flex items-center gap-2 w-full text-left px-2 py-1.5 rounded-lg hover:bg-zinc-100 transition-colors">
                {getDaySummaryIcon(group)}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-xs font-semibold text-zinc-700">
                      Day {group.dayNumber}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p className="text-xs font-medium">
                      {formatDateInTimezone(group.messages[0].scheduled_at, pcTimezone)}
                    </p>
                  </TooltipContent>
                </Tooltip>
                <span className="text-[11px] text-zinc-500 font-medium tabular-nums">
                  {summaryText}
                  {group.isComplete ? ' \u2713' : ''}
                </span>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="ml-4 pl-3 border-l-2 border-zinc-200 space-y-1 py-1">
                  {group.messages.map((msg) => {
                    const isNext = msg.id === nextMessageId;
                    const displayTime = msg.sent_at
                      ? formatTimeInTimezone(msg.sent_at, pcTimezone)
                      : formatTimeInTimezone(msg.scheduled_at, pcTimezone);
                    const statusLabel = getMessageStatusLabel(msg.status, isNext);

                    return (
                      <Tooltip key={msg.id}>
                        <TooltipTrigger asChild>
                          <div
                            className={cn(
                              'flex items-center gap-2 px-2 py-1 rounded text-xs cursor-default',
                              isNext && 'bg-zinc-50 border border-zinc-200',
                            )}
                          >
                            {getMessageStatusIcon(msg.status, isNext)}
                            <span className="text-zinc-600 font-medium">
                              {displayTime}
                            </span>
                            <span className="text-zinc-500">
                              &mdash; {statusLabel}
                            </span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent
                          side="right"
                          className="max-w-xs"
                        >
                          <p className="text-xs whitespace-pre-wrap">
                            {msg.message_text}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    );
                  })}
                </div>
              </CollapsibleContent>
            </Collapsible>
          );
        })}
      </div>
    </TooltipProvider>
  );
};
